import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { CgSpinner } from "react-icons/cg";
import { useNavigate } from "react-router-dom";
import axios from "../utils/axiosConfig";
import TableWithSearchAndPagination from "../components/TableWithSearchAndPagination"; // Import the table component

Modal.setAppElement("#root"); // For accessibility

const ProductsPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [newProduct, setNewProduct] = useState({
    product_alias: "",
    name: "",
    quantity: "",
    unit: "",
    description: "",
    extra_fields: {},
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [productsPerPage] = useState(10); // Fixed number of products per page
  const [extraFields, setExtraFields] = useState({
    extra_field_products: [],
    extra_field_batch: [],
  });
  const navigate = useNavigate();
  const [userData, setUserData] = useState({
    name: "",
    role: "",
    lastLogin: "",
    companyName: "",
  });

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axios.get("/protected");
        const { name, role, last_login, company_name } = response.data;
        setUserData({
          name,
          role,
          lastLogin: last_login,
          companyName: company_name,
        });
        if (role === "salesman") {
          navigate("/dashboard");
        }
        fetchProducts();
        fetchExtraFields();
      } catch (error) {
        console.error("Error fetching user data:", error);
        navigate("/login");
      }
    };

    const fetchProducts = async () => {
      try {
        const response = await axios.get("/products");
        setProducts(response.data);
        setFilteredProducts(response.data); // Initialize filtered products with all products
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching products:", error);
        setIsLoading(false);
      }
    };

    const fetchExtraFields = async () => {
      try {
        const response = await axios.get("/company-extra-fields");
        setExtraFields(response.data);
      } catch (error) {
        console.error("Error fetching extra fields:", error);
      }
    };

    fetchUserData();
  }, [navigate]);

  // Handle search functionality
  useEffect(() => {
    const filterProducts = () => {
      const filtered = products.filter(
        (product) =>
          (product.product_alias &&
            product.product_alias
              .toLowerCase()
              .includes(searchTerm.toLowerCase())) ||
          (product.name &&
            product.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (product.description &&
            product.description
              .toLowerCase()
              .includes(searchTerm.toLowerCase()))
      );
      setFilteredProducts(filtered);
    };

    filterProducts();
  }, [searchTerm, products]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewProduct({ ...newProduct, [name]: value });
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setNewProduct({ ...newProduct, [name]: files[0] });
  };

  const handleExtraFieldChange = (e) => {
    const { name, value } = e.target;
    setNewProduct((prevProduct) => ({
      ...prevProduct,
      extra_fields: { ...prevProduct.extra_fields, [name]: value },
    }));
  };

  const handlePageClick = (page) => {
    setCurrentPage(page);
  };

  const indexOfLastProduct = (currentPage + 1) * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(
    indexOfFirstProduct,
    indexOfLastProduct
  );

  const handleAddProduct = async () => {
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("product_alias", newProduct.product_alias);
      formData.append("name", newProduct.name);
      formData.append("quantity", newProduct.quantity);
      formData.append("unit", newProduct.unit);
      formData.append("description", newProduct.description);

      // Add extra fields to the form data as a JSON string
      formData.append('extra_fields', JSON.stringify(newProduct.extra_fields));

      // Add files to the form data if available
      if (newProduct.video_file) {
        formData.append("video_file", newProduct.video_file);
      }
      if (newProduct.image_file) {
        formData.append("image_file", newProduct.image_file);
      }
      if (newProduct.pdf_file) {
        formData.append("pdf_file", newProduct.pdf_file);
      }

      // Log the FormData contents
      console.log("New Product Data:");
      for (let pair of formData.entries()) {
        console.log(pair[0], pair[1]);
      }
      
      // Also log the raw newProduct object for comparison
      console.log("Raw newProduct object:", newProduct);

      await axios.post("/add-product", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setNewProduct({
        product_alias: "",
        name: "",
        quantity: "",
        unit: "",
        description: "",
        extra_fields: {},
        video_file: null,
        image_file: null,
        pdf_file: null,
      });
      setShowModal(false);
      setIsSubmitting(false);
      alert("Product Added Successfully");
      // Refetch products after adding
      const response = await axios.get("/products");
      setProducts(response.data);
    } catch (error) {
      console.error("Error adding product:", error);
      setIsSubmitting(false);
    }
  };

  // Table Headers
  const tableHeaders = ["Alias", "Name", "Quantity", "Unit", "Description"];

  // Table Rows (for mapping data to table cells)
  const tableRows = currentProducts.map((product) => [
    product.product_alias,
    product.name,
    product.quantity,
    product.unit,
    product.description || "N/A",
  ]);

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Sidebar userData={userData} />
      <div className="flex-1">
        {isLoading ? (
          <div className="flex justify-center items-center h-full">
            <CgSpinner className="animate-spin text-4xl" />
          </div>
        ) : (
          <>
            <Header userData={userData} />
            <div className="p-6">
              <TableWithSearchAndPagination
                tableHeaders={tableHeaders}
                tableRows={tableRows}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                buttonText="Add Product"
                onButtonClick={() => setShowModal(true)}
                pageCount={Math.ceil(filteredProducts.length / productsPerPage)}
                onPageChange={handlePageClick}
                currentPage={currentPage}
                userRole={userData.role}
              />

              <Modal
                isOpen={showModal}
                onRequestClose={() => setShowModal(false)}
                contentLabel="Add New Product"
                className="bg-white rounded shadow-lg w-1/2 mx-auto mt-10 relative"
                overlayClassName="fixed inset-0 bg-black bg-opacity-50"
              >
                {/* Close Button */}
                <button
                  onClick={() => setShowModal(false)}
                  className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                >
                  &times;
                </button>

                {/* Fixed Header */}
                <div className="sticky top-0 bg-white p-4 border-b border-gray-300 z-10">
                  <h2 className="text-xl font-bold">Add New Product</h2>
                </div>

                {/* Scrollable Content */}
                <div className="p-4 max-h-[70vh] overflow-y-auto">
                  {/* Input fields arranged in two columns */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Product Alias
                      </label>
                      <input
                        type="text"
                        name="product_alias"
                        value={newProduct.product_alias}
                        onChange={handleInputChange}
                        className="w-full p-2 border rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={newProduct.name}
                        onChange={handleInputChange}
                        className="w-full p-2 border rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Quantity
                      </label>
                      <input
                        type="number"
                        name="quantity"
                        value={newProduct.quantity}
                        onChange={handleInputChange}
                        className="w-full p-2 border rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Unit
                      </label>
                      <input
                        type="text"
                        name="unit"
                        value={newProduct.unit}
                        onChange={handleInputChange}
                        className="w-full p-2 border rounded"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium mb-1">
                        Description
                      </label>
                      <textarea
                        name="description"
                        value={newProduct.description}
                        onChange={handleInputChange}
                        className="w-full p-2 border rounded"
                      />
                    </div>
                  </div>

                  {/* File Upload Fields in Two Columns */}
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Video File
                      </label>
                      <input
                        type="file"
                        name="video_file"
                        onChange={handleFileChange}
                        accept="video/*"
                        className="w-full p-2 border rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Image File
                      </label>
                      <input
                        type="file"
                        name="image_file"
                        onChange={handleFileChange}
                        accept="image/*"
                        className="w-full p-2 border rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        PDF File
                      </label>
                      <input
                        type="file"
                        name="pdf_file"
                        onChange={handleFileChange}
                        accept="application/pdf"
                        className="w-full p-2 border rounded"
                      />
                    </div>
                  </div>

                  {/* Extra Fields Start from a New Row */}
                  {extraFields.extra_field_products.length > 0 && (
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      {extraFields.extra_field_products.map((field, index) => (
                        <div key={index}>
                          <label className="block text-sm font-medium mb-1">
                            {field}
                          </label>
                          <input
                            type="text"
                            name={field}
                            value={newProduct.extra_fields[field] || ""}
                            onChange={handleExtraFieldChange}
                            className="w-full p-2 border rounded"
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add Product Button (Full Width, Bold Text) */}
                  <div className="mt-6">
                    <button
                      onClick={handleAddProduct}
                      className={`w-full bg-teal-600 text-white font-bold px-4 py-3 rounded text-lg ${
                        isSubmitting
                          ? "opacity-50 cursor-not-allowed"
                          : "hover:bg-teal-700"
                      }`}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Adding..." : "ADD PRODUCT"}
                    </button>
                  </div>
                </div>
              </Modal>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProductsPage;
